import { useEffect, useRef, useState } from "react";

import { Input, InputGroup, InputGroupText } from "reactstrap";

//Unform e componentes
import { useField } from "@unform/core";
import TooltipItem from "../TooltipItem";

//Common
import { SHOW_DESC_OBRIGATORIO_FIELDS } from "@/common/constantes";
import { fn_removerAcentuacao } from "@/common/utils/util";

//Util
import { fn_validaKeyCampoNumerico } from "@/frontend/utils/form";
import { ICONES_ACESSO } from "@/frontend/utils/icones";

/**
 * This input component supports many <input> types, including:
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input
 *  - text
 *  - number
 *  - color
 *  - date
 *  - datetime-local
 *  - email
 *  - hidden
 *  - time
 *  - month
 *  - password
 *  - range
 *  - search
 *  - tel
 *  - url
 *  - week
 *
 * Don't use it with the type `submit` or `reset`; otherwise, bugs will occur.
 */
export default function UInput({
  name,
  type = "text",
  label,
  value,
  //defaultValue: defaultValueProp,
  tipo_valor,
  autoComplete,
  caseSensitive = false,
  lowerCase = false,
  removeAcentuacao = true,
  ariaLabel,
  textHelp,
  icone,
  footer,
  className = "",
  group = false,
  ...rest
}) {
  const inputRef = useRef(null);

  const [rangeValue, setRangeValue] = useState(value ?? 0);
  const [showPassword, setShowPassword] = useState(false);

  const { fieldName, defaultValue, registerField, error } = useField(name);
  const defaultInputValue = value ?? defaultValue;
  //const defaultInputValue = value ?? defaultValueProp ?? defaultValue;

  //const defaultAutoComplete = autoComplete || "off";

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef,

      getValue: (ref) => {
        return ref.current.value;
      },

      setValue: (ref, newValue) => {
        ref.current.value = newValue ?? "";
      },

      clearValue: (ref) => {
        ref.current.value = "";
      },
    });
  }, [fieldName, registerField]);

  //Importante para não apresentar valor antigo no campo e não ter que fazer gambiarra usando key no campo para atualizar o valor
  useEffect(() => {
    if (inputRef.current && value !== undefined) {
      inputRef.current.value = value ?? "";
    }
  }, [value]);

  //OnChange
  function handleChangeInput(event) {
    const input = event.target;
    let { value } = input;

    //posição atual do cursor
    const cursorPos = input.selectionStart;

    if (type === "range") {
      setRangeValue(value);
      return;
    }

    if (caseSensitive) {
      value = removeAcentuacao === true ? fn_removerAcentuacao(value) : value;
    } else {
      if (tipo_valor == "N") {
        value = fn_validaKeyCampoNumerico(value);
      } else {
        if (lowerCase === true) {
          value =
            removeAcentuacao === true
              ? fn_removerAcentuacao(value).toLowerCase()
              : value.toLowerCase();
        } else {
          value =
            removeAcentuacao === true
              ? fn_removerAcentuacao(value).toUpperCase()
              : value.toUpperCase();
        }
      }
    }

    input.value = value;

    // restaura cursor (no próximo frame pra evitar jump)
    if (type == "text") {
      requestAnimationFrame(() => {
        input.setSelectionRange(cursorPos, cursorPos);
      });
    }
  }

  if (group) {
    return (
      <input
        type={type}
        ref={inputRef}
        defaultValue={defaultInputValue}
        className={("form-control " + className).concat(error ? " error" : "")}
        {...rest}
      />
    );
  }

  return (
    <div className={label != null ? "form-group" : ""}>
      {label && (
        <label htmlFor={fieldName} className="label">
          {label}{" "}
          {textHelp && (
            <span>
              {" "}
              <TooltipItem text={textHelp} showImageHelp />{" "}
            </span>
          )}
        </label>
      )}

      <div className="d-flex align-items-center">
        {(icone && (
          <InputGroup>
            <InputGroupText>{icone}</InputGroupText>
            <Input
              type={
                type == "password" ? (showPassword ? "text" : "password") : type
              }
              innerRef={inputRef}
              id={fieldName}
              name={fieldName}
              aria-label={ariaLabel ? ariaLabel : label}
              defaultValue={defaultInputValue}
              autoComplete="new-password"
              className={`form-control ${className || ""} ${
                error ? "error" : ""
              }`}
              onChange={handleChangeInput}
              {...rest}
            />
            {type == "password" && (
              <InputGroupText
                onClick={() => setShowPassword(!showPassword)}
                style={{ cursor: "pointer" }}
              >
                {showPassword
                  ? ICONES_ACESSO.OLHO_ABERTO
                  : ICONES_ACESSO.OLHO_FECHADO}
              </InputGroupText>
            )}
          </InputGroup>
        )) || (
          <input
            type={type}
            ref={inputRef}
            id={fieldName}
            name={fieldName}
            aria-label={ariaLabel ? ariaLabel : label}
            defaultValue={defaultInputValue}
            autoComplete="one-time-code"
            className={`form-control ${className || ""} ${
              error && SHOW_DESC_OBRIGATORIO_FIELDS == false ? "error" : ""
            }`}
            onChange={handleChangeInput}
            {...rest}
          />
        )}

        {type === "range" && <span className="px-2 fw-bold">{rangeValue}</span>}
      </div>

      {footer && <div className="ms-1 mt-1 texto-menor">{footer}</div>}

      {error && SHOW_DESC_OBRIGATORIO_FIELDS == true && (
        <small className="text-danger">{error}</small>
      )}
    </div>
  );
}

/* Exemplos de criação de Input

   <Form ref={formRef} onSubmit={handleSubmit}>
      <Input label="Name" name="name" />
      <Input label="Choose a color" name="color" type="color" />
      <Input label="Choose a number" name="number" type="number" />
      <Input name="secret" type="hidden" value="teste" />
      <Input label="email" name="email" type="email" />
      <Input label="Month" name="month" type="month" min="2020-09" />
      <Input
        label="Telephone"
        name="telephone"
        type="tel"
        placeholder="Ex: XX-XXXXX-XXXX"
        pattern="[0-9]{2}-[0-9]{5}-[0-9]{4}"
      />
      <Input label="password" name="password" type="password" />
      <Input label="time" name="time" type="time" min="09:00" max="18:00" />
      <Input
        label="website"
        placeholder="https://example.com"
        pattern="https://.*"
        name="website"
        type="url"
      />
      <Input
        label="week"
        min="2021-W01"
        max="2021-W52"
        name="week"
        type="week"
      />
      <Input
        label="date"
        min="2021-01-01"
        max="2021-12-31"
        name="date"
        type="date"
      />
      <Input
        label="meeting-time"
        min="2020-06-07T00:00"
        max="2020-06-14T00:00"
        name="meeting"
        type="datetime-local"
      />
      <Input
        label="search"
        aria-label="Search through site content"
        name="search"
        type="search"
      />
      <Input type="range" name="volume" label="Volume" min="0" max="10" />

      <button type="submit">Submit</button>
    </Form>

*/
